import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Target, Download, Compass, Link2 } from "lucide-react";

const fmtVal = (v, unit) => {
  if (v === null || v === undefined) return "—";
  if (unit === "currency") {
    const a = Math.abs(v);
    if (a >= 1000000) return `$${(v / 1000000).toFixed(v % 1000000 === 0 ? 0 : 2)}M`;
    if (a >= 10000) return `$${Math.round(v / 1000)}k`;
    return `$${Math.round(v).toLocaleString()}`;
  }
  if (unit === "percent") return `${v}%`;
  return Number.isInteger(v) ? v.toLocaleString() : v.toFixed(1);
};
const PERIOD_LB = { weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly", ytd: "YTD" };

export default function ManagementGoals() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const { data } = useQuery({ queryKey: ["mgmt-goals"], queryFn: async () => (await api.get("/mgmt/annual-goals")).data });
  const goals = data?.goals || [];
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["mgmt-goals"] });
    qc.invalidateQueries({ queryKey: ["mgmt-scorecard"] });
  };

  const importMut = useMutation({
    mutationFn: async () => (await api.post("/mgmt/annual-goals/import-vision")).data,
    onSuccess: (d) => { invalidate(); toast.success(`Imported ${d.created} key number${d.created === 1 ? "" : "s"} from the Vision Planner${d.skipped ? ` (${d.skipped} already here or not numeric)` : ""}`); },
    onError: (e) => toast.error(e.response?.data?.detail || "Import failed"),
  });
  const saveMut = useMutation({
    mutationFn: async (f) => {
      const payload = { name: f.name, unit: f.unit, goal_op: f.goal_op, annual_target: parseFloat(f.annual_target) };
      if (f.id) return (await api.patch(`/mgmt/annual-goals/${f.id}`, payload)).data;
      return (await api.post("/mgmt/annual-goals", payload)).data;
    },
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Goal saved"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not save goal"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/mgmt/annual-goals/${id}`)).data,
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Goal removed"); },
  });
  const linkMut = useMutation({
    mutationFn: async ({ gid, metric_id }) => (await api.patch(`/mgmt/annual-goals/${gid}`, { metric_id })).data,
    onSuccess: invalidate,
    onError: (e) => toast.error(e.response?.data?.detail || "Could not link KPI"),
  });
  const applyMut = useMutation({
    mutationFn: async (gid) => (await api.post(`/mgmt/annual-goals/${gid}/apply`)).data,
    onSuccess: (d) => { invalidate(); toast.success(`KPI target updated to the suggested ${PERIOD_LB[d.period].toLowerCase()} goal`); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not apply target"),
  });
  const createKpiMut = useMutation({
    mutationFn: async (gid) => (await api.post(`/mgmt/annual-goals/${gid}/create-metric`)).data,
    onSuccess: () => { invalidate(); toast.success("YTD KPI created on the Scorecard"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not create KPI"),
  });

  const visionPending = (data?.vision_numbers || []).filter((v) => !v.imported && v.parsed_target != null);

  return (
    <div data-testid="management-goals-page" className="pb-24">
      <PageHeader
        title={`Annual Goals — ${data?.fy_label || "FY"}`}
        subtitle={`Driven by the Vision Planner 1-Year Plan · annual targets cascade into suggested quarterly, monthly & weekly KPI goals · ${data?.elapsed_pct ?? 0}% of the year elapsed`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => importMut.mutate()} disabled={importMut.isPending} data-testid="import-vision-btn">
              <Download size={14} className="mr-1" />Import from Vision
            </Button>
            <Button onClick={() => setEditing({ name: "", unit: "currency", goal_op: "gte", annual_target: "" })}
              className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="add-goal-btn"><Plus size={15} className="mr-1" />Add Goal</Button>
          </div>
        }
      />
      <div className="px-4 sm:px-6 lg:px-10 space-y-4">
        {visionPending.length > 0 && (
          <Card className="rounded-sm border-cyan-200 bg-cyan-50/50 shadow-none p-3.5 flex items-start gap-3" data-testid="vision-hint">
            <Compass size={16} className="text-cyan-700 mt-0.5 shrink-0" />
            <div className="text-xs text-slate-600">
              <span className="font-semibold text-slate-800">The Vision Planner 1-Year Plan has key numbers not yet here: </span>
              {visionPending.map((v) => `${v.label} (${v.value})`).join(" · ")}
              <span> — hit "Import from Vision" to bring them in.</span>
            </div>
          </Card>
        )}
        {goals.length === 0 ? (
          <Card className="rounded-sm border-slate-200 shadow-none p-8 text-center" data-testid="goals-empty">
            <Target size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">No annual goals yet.</p>
            <p className="text-xs text-slate-400 mt-1">Import your key numbers from the <Link to="/management/vision" className="text-cyan-700 hover:underline">Vision Planner 1-Year Plan</Link>, or add one manually.</p>
          </Card>
        ) : (
          goals.map((g) => {
            const prog = Math.max(0, Math.min(100, g.progress_pct ?? 0));
            return (
              <Card key={g.id} className="rounded-sm border-slate-200 shadow-none p-4" data-testid={`goal-card-${g.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900">{g.name}</span>
                      {g.vision_label && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-900 text-cyan-300">VISION · 1-YR</span>}
                      <button className="text-slate-400 hover:text-cyan-600 text-xs underline" onClick={() => setEditing({ ...g, annual_target: String(g.annual_target) })} data-testid={`edit-goal-${g.id}`}>edit</button>
                    </div>
                    <div className="font-mono text-lg font-bold text-slate-800 mt-0.5" data-testid={`goal-target-${g.id}`}>
                      {g.goal_op === "lte" ? "≤ " : "≥ "}{fmtVal(g.annual_target, g.unit)} <span className="text-[10px] font-sans font-semibold text-slate-400 uppercase tracking-wider">annual</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-center" data-testid={`goal-cascade-${g.id}`}>
                    {[["Quarterly", g.suggested?.quarterly], ["Monthly", g.suggested?.monthly], ["Weekly", g.suggested?.weekly]].map(([lb, v]) => (
                      <div key={lb} className="border border-slate-200 rounded-sm px-3 py-1.5 bg-slate-50">
                        <div className="text-[9px] uppercase tracking-wider font-bold text-slate-400">{lb}</div>
                        <div className="font-mono text-xs font-semibold text-slate-700">{g.goal_op === "lte" ? "≤" : "≥"} {fmtVal(v, g.unit)}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-2">
                    <Link2 size={13} className="text-slate-400" />
                    <Select value={g.metric_id || "none"} onValueChange={(v) => linkMut.mutate({ gid: g.id, metric_id: v === "none" ? null : v })}>
                      <SelectTrigger className="rounded-sm h-8 text-xs w-[230px]" data-testid={`goal-link-select-${g.id}`}><SelectValue placeholder="Link a scorecard KPI" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No linked KPI</SelectItem>
                        {(data?.metric_options || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {PERIOD_LB[m.period]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {g.metric ? (
                    <>
                      <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                        <span className="font-mono text-xs font-semibold text-slate-700 whitespace-nowrap" data-testid={`goal-ytd-${g.id}`}>YTD {fmtVal(g.ytd_actual, g.unit)}</span>
                        <div className="relative h-2 bg-slate-200 rounded-full flex-1 min-w-[120px]">
                          <div className={`absolute left-0 top-0 bottom-0 rounded-full ${g.on_pace ? "bg-emerald-600" : "bg-red-600"}`} style={{ width: `${prog}%` }} />
                          {g.unit !== "percent" && <div className="absolute w-0.5 bg-slate-900 -top-0.5 -bottom-0.5" style={{ left: `${Math.min(100, data?.elapsed_pct ?? 0)}%` }} />}
                        </div>
                        {g.on_pace != null && (
                          <span className={`text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${g.on_pace ? "text-emerald-700" : "text-red-700"}`} data-testid={`goal-pace-${g.id}`}>
                            {g.on_pace ? "On pace" : "Behind"}{g.progress_pct != null ? ` · ${g.progress_pct}%` : ""}
                          </span>
                        )}
                      </div>
                      <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => applyMut.mutate(g.id)} data-testid={`apply-goal-${g.id}`}>
                        Apply {PERIOD_LB[g.metric.period].toLowerCase()} target {g.goal_op === "lte" ? "≤" : "≥"} {fmtVal(g.metric.period === "ytd" ? g.annual_target : g.suggested?.[g.metric.period], g.unit)}
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => createKpiMut.mutate(g.id)} data-testid={`create-kpi-${g.id}`}>
                      <Plus size={12} className="mr-1" />Create YTD KPI on Scorecard
                    </Button>
                  )}
                </div>
              </Card>
            );
          })
        )}
        <p className="text-[11px] text-slate-400">
          Suggested targets are an even split of the annual goal (÷4 quarterly, ÷12 monthly, ÷52 weekly; % goals carry through unchanged).
          Link a KPI from the <Link to="/management/scorecard" className="text-cyan-700 hover:underline">Scorecard</Link> to see its live YTD figure against the annual goal, and apply the suggested target with one click.
        </p>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="goal-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Target size={16} className="text-cyan-600" />{editing?.id ? "Edit Annual Goal" : "New Annual Goal"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Goal name *</label>
                <Input value={editing.name} onChange={(e) => setEditing((f) => ({ ...f, name: e.target.value }))} className="rounded-sm mt-1" data-testid="goal-name-input" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Direction</label>
                  <Select value={editing.goal_op} onValueChange={(v) => setEditing((f) => ({ ...f, goal_op: v }))}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="goal-op-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gte">≥ at least</SelectItem>
                      <SelectItem value="lte">≤ at most</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Annual target *</label>
                  <Input type="number" value={editing.annual_target} onChange={(e) => setEditing((f) => ({ ...f, annual_target: e.target.value }))} className="rounded-sm mt-1 font-mono" data-testid="goal-target-input" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Unit</label>
                  <Select value={editing.unit} onValueChange={(v) => setEditing((f) => ({ ...f, unit: v }))}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="goal-unit-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="currency">$ Currency</SelectItem>
                      <SelectItem value="count">Count</SelectItem>
                      <SelectItem value="percent">% Percent</SelectItem>
                      <SelectItem value="hours">Hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            {editing?.id ? (
              <Button variant="ghost" className="text-red-600 hover:bg-red-50 rounded-sm" data-testid="delete-goal-btn"
                onClick={() => window.confirm("Remove this annual goal? (Linked KPIs stay on the scorecard)") && delMut.mutate(editing.id)}>
                <Trash2 size={15} className="mr-1" />Delete
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setEditing(null)} data-testid="cancel-goal-btn">Cancel</Button>
              <Button className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="save-goal-btn"
                disabled={!editing?.name?.trim() || editing?.annual_target === "" || saveMut.isPending}
                onClick={() => saveMut.mutate(editing)}>
                {editing?.id ? "Save changes" : "Add Goal"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
